/**
 * Promo Rails Manager
 * Injects promo rails after load more collection updates
 * Initial rails are server-rendered; this handles dynamic injection on 'collection:loadmore'
 */

class PromoRailsManager {
  constructor(railsConfig) {
    this.rails = railsConfig || [];
    this.observedRails = new WeakSet();
    this.init();
  }

  init() {
    // Observe server-rendered rails on page load
    this.observeAllRails();

    if (this.rails.length === 0) return;
    document.addEventListener('collection:loadmore', () => {
      setTimeout(() => {
        this.injectRails();
        // Observe newly injected rails
        this.observeAllRails();
      }, 250);
    });
  }

  /**
   * Observe all .zop-promo-rail elements for viewport visibility
   */
  observeAllRails() {
    const rails = document.querySelectorAll('.zop-promo-rail');
    rails.forEach(rail => {
      if (this.observedRails && !this.observedRails.has(rail)) {
        this.observedRails.add(rail);
        this.observeRailVisibility(rail);
      }
    });
  }

  /**
   * Set up IntersectionObserver for a single rail
   */
  observeRailVisibility(rail) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !rail._impressionTracked) {
            rail._impressionTracked = true;
            this.trackRailImpression(rail);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(rail);
  }

  /**
   * Send analytics when rail enters viewport
   */
  trackRailImpression(rail) {
    const railType = rail.getAttribute('data-rail-type');
    const railPosition = rail.getAttribute('data-rail-position');

    // Find rail config to get label
    const railConfig = this.rails.find(r => r.type === railType);
    const railTitle = railConfig?.title || railConfig?.brand_name;

    if (typeof sendAnalytics === 'function') {
      sendAnalytics('Promo Rail Viewed', railType, {
        rail_title: railTitle,
        rail_position: railPosition
      });
    } else {
      console.warn('sendAnalytics function not available');
    }
  }

  injectRails() {
    const productsList = document.querySelector('#ProductsList');
    if (!productsList) return;

    const productCards = this.getProductCards(productsList);

    this.rails.forEach(rail => {
      if (rail.enabled) {
        this.injectRail(productsList, productCards, rail);
      }
    });
  }

  getProductCards(productsList) {
    return Array.from(productsList.querySelectorAll('.card')).filter(
      card => !card.classList.contains('zop-promo-rail')
    );
  }

  injectRail(productsList, productCards, rail) {
    if (!productCards.length) return;

    const { start_position = 0, show_after = 9999, repeat_frequency = 1, type } = rail;
    const positions = this.calculatePositions(start_position, show_after, repeat_frequency, productCards.length);
    
    const handleizedType = type.toLowerCase().replace(/_/g, '-');
    const template = document.querySelector(`#${handleizedType}-rail-template`);
    if (!template?.content) return;
    
    const existingRails = productsList.querySelectorAll(`.zop-promo-rail--${handleizedType}`);
    const pendingPositions = positions.slice(existingRails.length);

    pendingPositions.forEach(pos => {
      if (pos > productCards.length) return;

      const railFragment = template.content.cloneNode(true);

      if (pos === 0) {
        productsList.insertBefore(railFragment, productsList.firstChild);
      } else {
        const currentProductCards = this.getProductCards(productsList);
        const targetCard = currentProductCards[pos - 1];
        if (targetCard) {
          targetCard.after(railFragment);
        }
      }
    });
  }

  calculatePositions(startPosition, showAfter, repeatFrequency, totalProducts) {
    const positions = [];
    
    for (let i = 0; i < repeatFrequency; i++) {
      const position = i === 0 ? startPosition : positions[i - 1] + showAfter;
      
      if (position <= totalProducts) {
        positions.push(position);
      } else {
        break;
      }
    }
    
    return positions;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const railsConfig = window.railConfig || [];
  window.promoRailsManager = new PromoRailsManager(railsConfig);
});

